import { CalendarDays, Clock4, MapPin, UsersRound } from "lucide-react";
import { formatDate, getDaysAgo } from "@/utils/dateUtils";
import { formatExperienceRange } from "@/utils/experience";
import { Card, CardFooter, CardHeader } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Link } from "react-router-dom";
import { useRef, useState, useLayoutEffect } from "react";

/**
 * Displays a single job card.
 *
 * @param {Object} job - Job data object
 */
const JobCard = ({ job }) => {
  const postedLabel = formatDate(job?.created_at);
  const jobDetailRoute = `/user/job-detail/${job.id}`;
  const experienceLabel = formatExperienceRange(job?.experience_min, job?.experience_max);

  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(job.skills.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const calculate = () => {
      const BUTTON_RESERVED = 90;
      const availableWidth = container.offsetWidth - BUTTON_RESERVED;

      const measurer = document.createElement("div");
      measurer.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;font-size:12px;padding:4px 12px;border-radius:8px;";
      document.body.appendChild(measurer);

      let usedWidth = 0;
      let count = 0;
      const BADGE_GAP = 6;
      const OVERFLOW_BADGE_WIDTH = 36;

      for (let i = 0; i < Math.min(job.skills.length, 4); i++) {
        measurer.textContent = job.skills[i];
        const badgeWidth = measurer.offsetWidth + BADGE_GAP;

        const isLast = i === job.skills.length - 1;
        const wouldNeedOverflow = !isLast;
        const spaceNeeded = wouldNeedOverflow ? usedWidth + badgeWidth + OVERFLOW_BADGE_WIDTH : usedWidth + badgeWidth;

        if (spaceNeeded > availableWidth) break;

        usedWidth += badgeWidth;
        count++;
      }

      document.body.removeChild(measurer);
      setVisibleCount(count);
    };

    calculate(); // run on mount

    const observer = new ResizeObserver(calculate);
    observer.observe(container);

    return () => observer.disconnect();
  }, [job.skills]);

  const visibleSkills = job.skills.slice(0, visibleCount);
  const remainingCount = job.skills.length - visibleCount;

  return (
    <Card className="bg-background-secondary flex flex-col gap-3 p-4 md:p-5 rounded-xl">
      {/* Header */}
      <CardHeader className="flex flex-col items-start p-0 sm:mb-2 gap-1">
        <h2 className="text-text-primary font-semibold text-lg md:text-xl leading-snug">
          {job.title}
        </h2>

        <div className="hidden md:flex flex-wrap items-center gap-2 md:gap-3 text-text-primary text-[12px]">
          {/* Location, first. Job titles are no longer unique per tenant, so a
              board can carry two open "Python Developer" cards at once and the
              deadline alone is not enough to tell them apart. */}
          {job?.location && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
            >
              <MapPin size={14} />
              {job.location}
            </a>
          )}

          {job?.shift_timing && (
            <span className="flex items-center gap-1">
              <Clock4 size={14} />
              {job.shift_timing}
            </span>
          )}

          {job?.calendar_type_name && (
            <span className="flex items-center gap-1">
              <CalendarDays size={14} />
              {job.calendar_type_name}
            </span>
          )}

          <span className="flex items-center gap-1">
            <UsersRound size={14} />
            Applicants: {job.candidate_count}
          </span>

          <span className="flex items-center gap-1">
            <Clock4 size={16} />
            Apply Before: {formatDate(job?.deadline)}
          </span>
        </div>
      </CardHeader>

      <div className="flex flex-wrap items-center md:text-xs text-[8px] text-text-primary py-1 my-3 bg-background-tertiary/40 rounded-lg">
        {/* The header row above is desktop-only, so the narrow layout carries
            location here instead. */}
        {job?.location ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="md:hidden flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary hover:underline"
          >
            <span>Location:</span> {job.location}
          </a>
        ) : (
          <span className="md:hidden flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
            <span>Location:</span> —
          </span>
        )}

        {job?.shift_timing && (
          <span className="md:hidden flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
            <span>Shift:</span> {job.shift_timing}
          </span>
        )}

        {job?.calendar_type_name && (
          <span className="md:hidden flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
            <span>Calendar:</span> {job.calendar_type_name}
          </span>
        )}

        <span className="md:hidden flex flex-col md:flex-col lg:flex-row items-center  gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
          <span>Applicants:</span> {job.candidate_count}
        </span>

        <span className="md:hidden flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
          <span>Apply Before:</span> {formatDate(job?.deadline)}
        </span>

        <span className="flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
          <span>Job Posted:</span> {formatDate(job?.created_at)}
        </span>

        <span className="flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-r border-border-primary">
          <span>Total Openings:</span> {job?.vacancy_count ?? 0}
        </span>

        {experienceLabel && (
          <span className="flex flex-col md:flex-col lg:flex-row items-center gap-1 py-1 px-2 md:px-4 border-l border-border-primary">
            <span>Experience:</span> {experienceLabel}
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-text-primary text-[14px] leading-relaxed line-clamp-3 flex-1">{job.role_overview}</p>

      {/* Footer */}
      <CardFooter ref={containerRef} className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 mt-1 p-0 overflow-hidden">
        {/* Skills row — never wraps */}
        <div className="flex flex-nowrap items-center gap-1.5 text-text-primary min-w-0 overflow-hidden w-full md:w-auto">
          {visibleSkills.map((skill, i) => (
            <Badge key={i} label={skill} variant="gradient" colorIndex={i % 8} />
          ))}

          {remainingCount > 0 && (
            <Link
              to={jobDetailRoute}
              className="inline-flex shrink-0 items-center px-3 py-2 rounded-lg text-[12px] font-medium bg-background-tertiary border border-text-base/10 transition-all duration-150"
            >
              +{remainingCount}
            </Link>
          )}
        </div>

        <Link to={jobDetailRoute} className="md:self-end self-start">
          <Button className="shrink-0 text-xs font-semibold bg-button-primary text-text-tertiary border border-gray-500 transition-all duration-150">View Details</Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

export default JobCard;
